import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Page from './pages/page/Page';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import VerifyPage from './pages/auth/VerifyPage';
import ForgotPasswordPages from './pages/auth/ForgotPasswordPages';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AcceptInvitationPage from './pages/auth/AcceptInvitationPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import BlankDashboard from './pages/dashboard/BlankDashboard';
import CreateNewSpace from './pages/spaces/CreateNewSpace';
import { SettingsPage } from './pages/settings/SettingsPage';
import MobileInstallPage from './pages/mobile-install/MobileInstallPage';
import { CheckInAlertsSettings } from './pages/settings/CheckInAlertsSettings';
import { SpaceCheckInAlertsSettings } from './pages/settings/SpaceCheckInAlertsSettings';
import { ProfilePage } from './pages/profile/ProfilePage';
import { PublicProfilePage } from './pages/profile/PublicProfilePage';
import { CompanyTeamPage } from './pages/company-team/CompanyTeamPage';
import { EditMemberPage } from './pages/company-team/EditMemberPage';
import { CompanySettingsPage } from './pages/company-settings/CompanySettingsPage';
import { CompanyAlertSettingsPage } from './pages/company-alert-settings/CompanyAlertSettingsPage';
import { SpaceAlertSettingsPage } from './pages/space-alert-settings/SpaceAlertSettingsPage';
import { ProjectDetailPage } from './pages/project/ProjectDetailPage';
import MatrixPage from './pages/matrix/MatrixPage';
import { GlobalMatrixPage } from './pages/matrix/GlobalMatrixPage';
import CompliancePage from './pages/compliance/CompliancePage';
import SiteCompliancePage from './pages/compliance/SiteCompliancePage';
import QualificationsPage from './pages/requirements/QualificationsPage';
import { QualificationsPage as GlobalQualificationsPage } from './pages/qualifications/QualificationsPage';
import { TrainersPage } from './pages/trainers/TrainersPage';
import { TrainerProfilePage } from './pages/trainers/TrainerProfilePage';
import { QuoteBuilderPage } from './pages/quotes/QuoteBuilderPage';
import TeamPage from './pages/team/TeamPage';
import { EditTeamMemberPage } from './pages/team/EditTeamMemberPage';
// ManageRequirementsPage removed - functionality moved to ProfilePage qualifications tab
import ChatPage from './pages/chat/ChatPage';
import BookingsTable from './pages/bookings/BookingsTable';
import ViewBookingPage from './pages/bookings/ViewBookingPage';
import ChecklistsPage from './pages/checklists/ChecklistsPage';
import ChecklistTemplatesPage from './pages/checklists/ChecklistTemplatesPage';
import ChecklistDetailPage from './pages/checklists/ChecklistDetailPage';
import MessageBoardPage from './pages/message-board/MessageBoardPage';
import { CreatePostPage } from './pages/message-board/CreatePostPage';
import { PostTrackingPage } from './pages/message-board/PostTrackingPage';
import { GenerateQuizPage } from './pages/message-board/GenerateQuizPage';
import DocsAndFilesPage from './pages/docs/DocsAndFilesPage';
import SchedulePageV2 from './pages/schedule/SchedulePageV2';
import { CheckInPage } from './pages/checkin/CheckInPage';
import { MobileCheckInPage } from './pages/checkin/MobileCheckInPage';
import { AttendanceRouter } from './pages/attendance/AttendanceRouter';
import { GlobalPostsPage } from './pages/posts/GlobalPostsPage';
import { GlobalDocsPage } from './pages/docs/GlobalDocsPage';
import ProcessesPage from './pages/processes/ProcessesPage';
import ProcessManagementPage from './pages/processes/ProcessManagementPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PPEManagementPage } from './pages/ppe/PPEManagementPage';
import TasksPage from './pages/tasks/TasksPage';
import CustomToolPage from './pages/custom-tool/CustomToolPage';
import WorkflowEditorPage from './pages/tasks/WorkflowEditorPage';
import ScheduleEditorPage from './pages/tasks/ScheduleEditorPage';
import ScheduleSummaryPage from './pages/tasks/ScheduleSummaryPage';
import WorkflowRunPage from './pages/tasks/WorkflowRunPage';
import WorkflowManagementPage from './pages/tasks/WorkflowManagementPage';
import WorkflowReportsPage from './pages/tasks/WorkflowReportsPage';
import WorkflowPreviewPage from './pages/tasks/WorkflowPreviewPage';
import { PageEditorPage } from './pages/tasks/PageEditorPage';
import { PageViewerPage } from './pages/tasks/PageViewerPage';
import { useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ChatFlowsWidget } from './components/ChatFlowsWidget';
import { StackLandingPage } from './pages/stack/StackLandingPage';
import { StackMatrixPage } from './pages/stack/StackMatrixPage';
import { StackTeamPage } from './pages/stack/StackTeamPage';
import { StackQualificationsPage } from './pages/stack/StackQualificationsPage';
import { StackBookingsPage } from './pages/stack/StackBookingsPage';
import { StackSchedulePage } from './pages/stack/StackSchedulePage';
import { StackChecklistsPage } from './pages/stack/StackChecklistsPage';
import { StackDocsPage } from './pages/stack/StackDocsPage';
import { StackMessageBoardPage } from './pages/stack/StackMessageBoardPage';
import { StackCheckinsPage } from './pages/stack/StackCheckinsPage';
import { AI_CONFIG, AiProvider } from './ai-editor';
import { supabase } from './lib/supabase';
import { BannerGeneratorPage } from './pages/BannerGeneratorPage';
import DashboardTestPage from './pages/dashboard-test/DashboardTestPage';
import AiGenAnimDebugPage from './pages/debug/AiGenAnimDebugPage';
import DashboardTestGridPage from './pages/dashboard-test-grid/DashboardTestGridPage';
import DemoPage from './pages/demo/DemoPage';
import PricingPage from './pages/pricing/PricingPage';
import { SmsCreditsPage } from './pages/sms-credits/SmsCreditsPage';
import KioskApp from './pages/kiosk/KioskApp';
import KioskPairingPage from './pages/kiosk/KioskPairingPage';
import ImportMatrixPage from './pages/import-matrix/ImportMatrixPage';
import CreateCompanyPage from './pages/create-company/CreateCompanyPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

// Manager/Admin Only Route - redirects "User" role to their profile
const ManagerOrAdminRoute = ({ children }) => {
  const { user, loading, userRole, userProfile } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user role is "User" (not Admin or Manager), redirect to their profile
  if (userRole === 'User') {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

// Profile Route - allows users to only view their own profile
const ProfileRoute = ({ children }) => {
  const { user, loading, userRole, userProfile } = useAuth();
  const { userId } = useParams();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If viewing someone else's profile and user is "User" role, redirect to own profile
  if (userId && userRole === 'User' && userId !== userProfile?.id) {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

// Smart Home Redirect - redirects to appropriate page based on user role
const HomeRedirect = () => {
  const { user, loading, userRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // "User" role goes to profile, others go to spaces
  if (userRole === 'User') {
    return <Navigate to="/profile" replace />;
  }

  return <Navigate to="/spaces" replace />;
};

// Redirect component for space checkin-alerts → alert-settings
const SpaceCheckinAlertsRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/space/${projectId}/alert-settings`} replace />;
};

// Public Route component (redirects to appropriate page if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, userRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (user) {
    // "User" role goes to profile, others go to spaces
    return <Navigate to={userRole === 'User' ? '/profile' : '/spaces'} replace />;
  }

  return children;
};

// Space Protected Route - checks space access BEFORE rendering any content
// Also restricts "User" role from accessing any spaces
const SpaceProtectedRoute = ({ children }) => {
  const { projectId } = useParams();
  const { user, loading, canAccessSpace, userProfile, userRole } = useAuth();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAccess = async () => {
      if (!user || !userProfile?.id || !projectId) {
        setChecking(false);
        return;
      }

      // If user role is "User", they cannot access any spaces
      if (userRole === 'User') {
        navigate('/profile', { replace: true });
        setChecking(false);
        return;
      }

      try {
        const access = await canAccessSpace(projectId);
        setHasAccess(access);
        if (!access) {
          navigate('/spaces', { replace: true });
        }
      } catch (error) {
        console.error('Error checking space access:', error);
        navigate('/spaces', { replace: true });
      } finally {
        setChecking(false);
      }
    };

    if (!loading && user && userProfile?.id) {
      checkAccess();
    } else if (!loading && !user) {
      setChecking(false);
    }
  }, [user, loading, userProfile?.id, projectId, canAccessSpace, navigate, userRole]);

  // Show loading while auth or access check is in progress
  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect "User" role to profile
  if (userRole === 'User') {
    return <Navigate to="/profile" replace />;
  }

  // Only render children if access is granted
  return hasAccess ? children : null;
};

const AppRoutes = () => {
  const location = useLocation();

  // Only trigger animations on main route changes, not sub-routes
  // This prevents re-rendering when navigating within attendance/* routes
  const animationKey = location.pathname.split('/').slice(0, 4).join('/');

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={animationKey}>
        {/* Auth Routes - Public only */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
        <Route path="/verify" element={<PublicRoute><VerifyPage /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPages /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/demo" element={<PublicRoute><DemoPage /></PublicRoute>} />
        <Route path="/pricing" element={<PricingPage />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<HomeRedirect />} />
        <Route path="/spaces" element={<ManagerOrAdminRoute><DashboardPage /></ManagerOrAdminRoute>} />
        <Route path="/create-space" element={<ManagerOrAdminRoute><CreateNewSpace /></ManagerOrAdminRoute>} />
        <Route path="/create-company" element={<ProtectedRoute><CreateCompanyPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProfileRoute><ProfilePage /></ProfileRoute>} />
        <Route path="/company-team" element={<ManagerOrAdminRoute><CompanyTeamPage /></ManagerOrAdminRoute>} />
        <Route path="/company-team/edit/:memberId" element={<ManagerOrAdminRoute><EditMemberPage /></ManagerOrAdminRoute>} />
        <Route path="/company-settings" element={<ManagerOrAdminRoute><CompanySettingsPage /></ManagerOrAdminRoute>} />
        <Route path="/company-settings/alerts" element={<ManagerOrAdminRoute><CompanyAlertSettingsPage /></ManagerOrAdminRoute>} />
        <Route path="/company-settings/sms-credits" element={<ManagerOrAdminRoute><SmsCreditsPage /></ManagerOrAdminRoute>} />
        <Route path="/matrix" element={<ManagerOrAdminRoute><GlobalMatrixPage /></ManagerOrAdminRoute>} />
        <Route path="/qualifications" element={<ManagerOrAdminRoute><GlobalQualificationsPage /></ManagerOrAdminRoute>} />
        <Route path="/trainers" element={<ManagerOrAdminRoute><TrainersPage /></ManagerOrAdminRoute>} />
        <Route path="/trainers/:trainerId" element={<ManagerOrAdminRoute><TrainerProfilePage /></ManagerOrAdminRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/get-mobile-app" element={<ProtectedRoute><MobileInstallPage /></ProtectedRoute>} />
        {/* Redirect old checkin-alerts to unified alerts page */}
        <Route path="/company-settings/checkin-alerts" element={<Navigate to="/company-settings/alerts" replace />} />
        <Route path="/bookings" element={<ManagerOrAdminRoute><BookingsTable /></ManagerOrAdminRoute>} />
        <Route path="/bookings/:bookingId" element={<ManagerOrAdminRoute><ViewBookingPage /></ManagerOrAdminRoute>} />
        <Route path="/checklists" element={<ManagerOrAdminRoute><ChecklistsPage /></ManagerOrAdminRoute>} />
        <Route path="/checklist-templates" element={<ManagerOrAdminRoute><ChecklistTemplatesPage /></ManagerOrAdminRoute>} />
        <Route path="/checklist/:checklistId" element={<ManagerOrAdminRoute><ChecklistDetailPage /></ManagerOrAdminRoute>} />
        <Route path="/attendance/*" element={<ProtectedRoute><AttendanceRouter /></ProtectedRoute>} />
        <Route path="/posts" element={<ProtectedRoute><GlobalPostsPage /></ProtectedRoute>} />
        <Route path="/posts/create" element={<ManagerOrAdminRoute><CreatePostPage /></ManagerOrAdminRoute>} />
        <Route path="/posts/generate-quiz" element={<ManagerOrAdminRoute><GenerateQuizPage /></ManagerOrAdminRoute>} />
        <Route path="/posts/:postId/tracking" element={<ManagerOrAdminRoute><PostTrackingPage /></ManagerOrAdminRoute>} />
        <Route path="/posts/:postId" element={<ProtectedRoute><GlobalPostsPage /></ProtectedRoute>} />

        {/* Global Docs Routes - Company-level documents view */}
        <Route path="/docs" element={<ManagerOrAdminRoute><GlobalDocsPage /></ManagerOrAdminRoute>} />
        <Route path="/docs/:folder" element={<ManagerOrAdminRoute><GlobalDocsPage /></ManagerOrAdminRoute>} />

        {/* Process Routes - Company-level process library (Admin only) */}
        <Route path="/processes" element={<ManagerOrAdminRoute><ProcessesPage /></ManagerOrAdminRoute>} />
        <Route path="/processes/editor" element={<ManagerOrAdminRoute><WorkflowEditorPage /></ManagerOrAdminRoute>} />
        <Route path="/processes/editor/:processId" element={<ManagerOrAdminRoute><WorkflowEditorPage /></ManagerOrAdminRoute>} />
        <Route path="/processes/:processId" element={<ManagerOrAdminRoute><ProcessManagementPage /></ManagerOrAdminRoute>} />

        {/* Space Routes - use SpaceProtectedRoute to check access BEFORE rendering */}
        <Route path="/space/:projectId" element={<SpaceProtectedRoute><ProjectDetailPage /></SpaceProtectedRoute>} />
        <Route path="/matrix/:projectId" element={<SpaceProtectedRoute><MatrixPage /></SpaceProtectedRoute>} />
        <Route path="/compliance/:projectId" element={<SpaceProtectedRoute><CompliancePage /></SpaceProtectedRoute>} />
        <Route path="/team/:projectId" element={<SpaceProtectedRoute><TeamPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/matrix" element={<SpaceProtectedRoute><MatrixPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/import-matrix" element={<SpaceProtectedRoute><ImportMatrixPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/compliance" element={<SpaceProtectedRoute><CompliancePage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/site-compliance" element={<SpaceProtectedRoute><SiteCompliancePage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/team" element={<SpaceProtectedRoute><TeamPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/team/edit/:memberId" element={<SpaceProtectedRoute><EditTeamMemberPage /></SpaceProtectedRoute>} />
        {/* ManageRequirementsPage route removed - functionality moved to /profile/:userId?tab=qualifications&spaceId=xxx */}
        <Route path="/space/:projectId/qualifications" element={<SpaceProtectedRoute><QualificationsPage /></SpaceProtectedRoute>} />
        {/* SpaceChatPage removed — chat consolidated to /chat?space=projectId */}
        <Route path="/space/:projectId/bookings" element={<SpaceProtectedRoute><BookingsTable /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/bookings/:bookingId" element={<SpaceProtectedRoute><ViewBookingPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/schedule" element={<SpaceProtectedRoute><SchedulePageV2 /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/checklists" element={<SpaceProtectedRoute><ChecklistsPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/checklist-templates" element={<SpaceProtectedRoute><ChecklistTemplatesPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/message-board" element={<SpaceProtectedRoute><MessageBoardPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/message-board/create" element={<SpaceProtectedRoute><CreatePostPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/message-board/:postId/tracking" element={<SpaceProtectedRoute><PostTrackingPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/message-board/:postId" element={<SpaceProtectedRoute><MessageBoardPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/docs" element={<SpaceProtectedRoute><DocsAndFilesPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/docs/:folder" element={<SpaceProtectedRoute><DocsAndFilesPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/checkins" element={<SpaceProtectedRoute><CheckInPage /></SpaceProtectedRoute>} />
        {/* Redirect old checkin-alerts to unified alert-settings page */}
        <Route path="/space/:projectId/checkin-alerts" element={<SpaceCheckinAlertsRedirect />} />
        <Route path="/space/:projectId/alert-settings" element={<SpaceProtectedRoute><SpaceAlertSettingsPage /></SpaceProtectedRoute>} />
        {/* Attendance is now global only - removed space-level route */}
        <Route path="/space/:projectId/build-quote" element={<SpaceProtectedRoute><QuoteBuilderPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/ppe" element={<SpaceProtectedRoute><PPEManagementPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/tasks" element={<SpaceProtectedRoute><TasksPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/workflow-editor" element={<SpaceProtectedRoute><WorkflowEditorPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/workflow-preview/:workflowId" element={<SpaceProtectedRoute><WorkflowPreviewPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/library/:workflowId" element={<SpaceProtectedRoute><WorkflowManagementPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/workflow-run/:runId" element={<SpaceProtectedRoute><WorkflowRunPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/workflow-reports" element={<SpaceProtectedRoute><WorkflowReportsPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/page-editor" element={<SpaceProtectedRoute><PageEditorPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/page/:pageId" element={<SpaceProtectedRoute><PageViewerPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/schedule-editor" element={<SpaceProtectedRoute><ScheduleEditorPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/schedule-summary" element={<SpaceProtectedRoute><ScheduleSummaryPage /></SpaceProtectedRoute>} />
        <Route path="/space/:projectId/tool/:toolSlug" element={<SpaceProtectedRoute><CustomToolPage /></SpaceProtectedRoute>} />
        <Route path="/tasks/page-editor" element={<ManagerOrAdminRoute><PageEditorPage /></ManagerOrAdminRoute>} />
        <Route path="/tasks/schedule-editor" element={<ManagerOrAdminRoute><ScheduleEditorPage /></ManagerOrAdminRoute>} />
        <Route path="/tasks/schedule-summary" element={<ManagerOrAdminRoute><ScheduleSummaryPage /></ManagerOrAdminRoute>} />

        {/* Stack Routes - show landing page with combined tools preview */}
        <Route path="/stack/:stackId" element={<ManagerOrAdminRoute><StackLandingPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/matrix" element={<ManagerOrAdminRoute><StackMatrixPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/team" element={<ManagerOrAdminRoute><StackTeamPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/qualifications" element={<ManagerOrAdminRoute><StackQualificationsPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/bookings" element={<ManagerOrAdminRoute><StackBookingsPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/bookings/:bookingId" element={<ManagerOrAdminRoute><ViewBookingPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/schedule" element={<ManagerOrAdminRoute><StackSchedulePage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/checklists" element={<ManagerOrAdminRoute><StackChecklistsPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/docs" element={<ManagerOrAdminRoute><StackDocsPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/docs/:folder" element={<ManagerOrAdminRoute><StackDocsPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/message-board" element={<ManagerOrAdminRoute><StackMessageBoardPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/message-board/create" element={<ManagerOrAdminRoute><CreatePostPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/message-board/:postId" element={<ManagerOrAdminRoute><StackMessageBoardPage /></ManagerOrAdminRoute>} />
        <Route path="/stack/:stackId/checkins" element={<ManagerOrAdminRoute><StackCheckinsPage /></ManagerOrAdminRoute>} />

        {/* Mobile Check-In Routes - Semi-Public (requires auth but different layout) */}
        <Route path="/check-in/:qrCode" element={<ProtectedRoute><MobileCheckInPage /></ProtectedRoute>} />
        <Route path="/checkin/:qrCode" element={<ProtectedRoute><MobileCheckInPage /></ProtectedRoute>} />

        {/* Kiosk Routes - Device-authenticated, no user session required */}
        <Route path="/kiosk/pair" element={<KioskPairingPage />} />
        <Route path="/kiosk/:deviceToken" element={<KioskApp />} />

        {/* Test/Dev Routes */}
        <Route path="/test/banner-generator" element={<ManagerOrAdminRoute><BannerGeneratorPage /></ManagerOrAdminRoute>} />
        <Route path="/aigenanim-debug" element={<AiGenAnimDebugPage />} />
        <Route path="/dashboard-test" element={<ManagerOrAdminRoute><DashboardTestPage /></ManagerOrAdminRoute>} />
        <Route path="/dashboard-test-grid" element={<ManagerOrAdminRoute><DashboardTestGridPage /></ManagerOrAdminRoute>} />

        {/* Root redirect - smart redirect based on user role */}
        <Route path="/" element={<HomeRedirect />} />

        {/* 404 Page - Must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
};

const handleAiRequest = async ({ template, selectedText, customPrompt, customFileData }) => {
  const { data: response } = await supabase.functions.invoke(
    AI_CONFIG.EDGE_FUNCTIONS.TEXT_COMPLETION,
    {
      body: {
        model: AI_CONFIG.MODEL,
        details: { template },
        input: { content: selectedText, prompt: customPrompt },
        ...(customFileData && { file: customFileData })
      }
    }
  );
  return response;
};

const handleVoiceRequest = async (audioBlob) => {
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const { data: response } = await supabase.functions.invoke(
    AI_CONFIG.EDGE_FUNCTIONS.VOICE_COMPLETION,
    { body: formData }
  );
  return response;
};

// Wrapper component that handles profile/company switching with smooth transitions
const SwitchableAppContent = () => {
  const { switchKey, isSwitching } = useAuth();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={switchKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="min-h-screen"
      >
        {/* Overlay during switching for smooth visual */}
        {isSwitching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center"
          >
            <LoadingSpinner />
          </motion.div>
        )}
        <AppRoutes />
      </motion.div>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <AiProvider
      handleAiRequest={handleAiRequest}
      handleVoiceRequest={handleVoiceRequest}
    >
      <SwitchableAppContent />
      <Toaster position="top-right" richColors />
      <ChatFlowsWidget />
    </AiProvider>
  );
};

export default App;
